import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  FilterList,
  Visibility,
  Edit,
  Block,
  Delete,
  Add,
  Refresh,
  AccountBalanceWallet,
  Person,
  TrendingUp,
  Receipt,
  Group,
  Settings,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  fetchUsers,
  fetchUserDetails,
  updateUserWallet,
  blockUser,
  unblockUser,
  deleteUser,
  setFilters,
  clearFilters,
  setPage,
} from '../store/slices/usersSlice';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import UserDetailsModal from '../components/users/UserDetailsModal';
import WalletUpdateModal from '../components/users/WalletUpdateModal';
import SetWalletModal from '../components/users/SetWalletModal';
import BlockUnblockModal from '../components/users/BlockUnblockModal';
import UserTransactionsModal from '../components/users/UserTransactionsModal';
import UserInvitesModal from '../components/users/UserInvitesModal';
import { CompleteUser } from '../types';

const UsersPage: React.FC = () => {
  const dispatch = useDispatch();
  const { users, pagination, statistics, loading, error } = useSelector(
    (state: RootState) => state.users
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedUser, setSelectedUser] = useState<CompleteUser | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showWalletUpdate, setShowWalletUpdate] = useState(false);
  const [showSetWallet, setShowSetWallet] = useState(false);
  const [showBlockUnblock, setShowBlockUnblock] = useState(false);
  const [blockUnblockAction, setBlockUnblockAction] = useState<'block' | 'unblock'>('block');
  const [blockUnblockLoading, setBlockUnblockLoading] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  const [showInvites, setShowInvites] = useState(false);

  useEffect(() => {
    dispatch(fetchUsers({ page: 1, limit: 20 }) as any);
  }, [dispatch]);

  const handleSearch = () => {
    const filters: any = {};
    if (searchTerm) filters.search = searchTerm;
    if (statusFilter) filters.status = statusFilter;
    dispatch(setFilters(filters));
    dispatch(fetchUsers({ page: 1, limit: 20, filters }) as any);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
    dispatch(fetchUsers({ page: newPage + 1, limit: 20 }) as any);
  };

  const handleViewUser = async (userId: string) => {
    try {
      const result = await dispatch(fetchUserDetails(userId) as any);
      if (result.payload) {
        setSelectedUser(result.payload);
        setShowUserDetails(true);
      }
    } catch (error) {
      toast.error('Failed to fetch user details');
    }
  };

  const handleAddBalance = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setShowWalletUpdate(true);
    }
  };

  const handleSetWallet = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setShowSetWallet(true);
    }
  };

  const handleBlockUser = async (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setBlockUnblockAction('block');
      setShowBlockUnblock(true);
    }
  };

  const handleUnblockUser = async (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setBlockUnblockAction('unblock');
      setShowBlockUnblock(true);
    }
  };

  const handleBlockUnblockConfirm = async (reason?: string) => {
    if (!selectedUser) return;

    setBlockUnblockLoading(true);
    try {
      const action = blockUnblockAction === 'block' 
        ? blockUser({ userId: selectedUser.id, reason }) 
        : unblockUser({ userId: selectedUser.id, reason });
      
      const result = await dispatch(action as any);
      
      if (result.payload) {
        toast.success(`User ${blockUnblockAction}ed successfully`);
        dispatch(fetchUsers({ page: pagination.page, limit: 20 }) as any);
        setShowBlockUnblock(false);
      }
    } catch (error) {
      toast.error(`Failed to ${blockUnblockAction} user`);
    } finally {
      setBlockUnblockLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const result = await dispatch(deleteUser(userId) as any);
        if (result.payload?.success) {
          toast.success('User deleted successfully');
          dispatch(fetchUsers({ page: pagination.page, limit: 20 }) as any);
        }
      } catch (error) {
        toast.error('Failed to delete user');
      }
    }
  };

  const handleViewTransactions = (user: CompleteUser) => {
    setSelectedUser(user);
    setShowTransactions(true);
  };

  const handleViewInvites = (user: CompleteUser) => {
    setSelectedUser(user);
    setShowInvites(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
    }).format(amount);
  };

  const getStatusColor = (status?: string) => {
    if (!status) return 'default';
    return status === 'active' ? 'success' : 'error';
  };

  const getStatusLabel = (status?: string) => {
    if (!status) return 'Unknown';
    return status === 'active' ? 'Active' : 'Inactive';
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: "1.5rem", sm: "2rem" }, mb: { xs: 1, sm: 2 } }}>
        Users Management
      </Typography>

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 2, md: 3 }, mb: { xs: 2, sm: 3 } }}>
        <Box sx={{ flex: '1 1 140px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Person sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h6">{statistics.totalUsers}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 140px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUp sx={{ mr: 2, color: 'success.main' }} />
                <Box>
                  <Typography variant="h6">{statistics.activeUsers}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Active Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 140px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AccountBalanceWallet sx={{ mr: 2, color: 'warning.main' }} />
                <Box>
                  <Typography variant="h6">
                    {formatCurrency(statistics.totalWalletBalance)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Wallet Balance
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 140px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Add sx={{ mr: 2, color: 'info.main' }} />
                <Box>
                  <Typography variant="h6">{users.length}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Current Page
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, flex: 1 }}>
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
            <FormControl fullWidth sx={{ minWidth: { xs: '100%', sm: 200 } }} size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 1, 
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            {/* First row of buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Search />}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  dispatch(clearFilters());
                  dispatch(fetchUsers({ page: 1, limit: 20 }) as any);
                }}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Clear
              </Button>
            </Box>
            
            {/* Second row of buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                variant="outlined"
                onClick={() => setShowFilters(!showFilters)}
                startIcon={<FilterList />}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Filters
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Users Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table sx={{ minWidth: { xs: 520, md: 800 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Username</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Telegram</TableCell>
                <TableCell>Wallet</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Games</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {user.username}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {user.referralCode}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {user.phoneNumber || '-'}
                      </Typography>
                      <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="textSecondary">
                          {user.telegramUsername || 'No Telegram'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {user.gamesPlayed} games
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {user.telegramUsername || '-'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(user.wallet)}
                      </Typography>
                      <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={getStatusLabel(user.status)}
                          color={getStatusColor(user.status) as any}
                          size="small"
                        />
                        <Typography variant="caption" color="textSecondary">
                          {dayjs(user.createdAt).format('MMM DD')}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    <Typography variant="body2">
                      {user.gamesPlayed} / {user.gamesWon}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {user.gamesPlayed > 0
                        ? `${((user.gamesWon / user.gamesPlayed) * 100).toFixed(1)}% win rate`
                        : 'No games'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        label={getStatusLabel(user.status)}
                        color={getStatusColor(user.status) as any}
                        size="small"
                      />
                      <Typography variant="caption" color="textSecondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                        {dayjs(user.createdAt).format('MMM DD, YYYY')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    {dayjs(user.createdAt).format('MMM DD, YYYY')}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewUser(user.id)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Add Balance">
                        <IconButton
                          size="small"
                          onClick={() => handleAddBalance(user.id)}
                          color="success"
                        >
                          <AccountBalanceWallet />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Set Wallet Balance">
                        <IconButton
                          size="small"
                          onClick={() => handleSetWallet(user.id)}
                          color="primary"
                        >
                          <Settings />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Transactions">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleViewTransactions(user)}
                        >
                          <Receipt />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Invites">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleViewInvites(user)}
                        >
                          <Group />
                        </IconButton>
                      </Tooltip>
                      {user.status === 'active' || (!user.status && user.gamesPlayed > 0) ? (
                        <Tooltip title="Block User">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleBlockUser(user.id)}
                          >
                            <Block />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Unblock User">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleUnblockUser(user.id)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete User">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.limit}
          rowsPerPageOptions={[10, 20, 50]}
          sx={{
            '& .MuiTablePagination-toolbar': {
              flexDirection: 'row',
              gap: { xs: 0.5, sm: 1 },
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            },
            '& .MuiTablePagination-spacer': {
              display: 'none',
            },
            '& .MuiTablePagination-selectLabel': {
              display: 'none',
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              order: { xs: 2, sm: 0 },
            },
            '& .MuiTablePagination-select': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              order: { xs: 1, sm: 0 },
            },
            '& .MuiTablePagination-actions': {
              order: { xs: 3, sm: 0 },
            },
          }}
        />
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* User Details Modal */}
      <UserDetailsModal
        user={selectedUser}
        open={showUserDetails}
        onClose={() => setShowUserDetails(false)}
        onWalletUpdate={() => {
          setShowUserDetails(false);
          setShowWalletUpdate(true);
        }}
      />

      {/* Wallet Update Modal */}
      <WalletUpdateModal
        user={selectedUser}
        open={showWalletUpdate}
        onClose={() => setShowWalletUpdate(false)}
        onSuccess={() => {
          dispatch(fetchUsers({ page: pagination.page, limit: 20 }) as any);
        }}
      />

      {/* Set Wallet Modal */}
      <SetWalletModal
        user={selectedUser}
        open={showSetWallet}
        onClose={() => setShowSetWallet(false)}
        onSuccess={() => {
          dispatch(fetchUsers({ page: pagination.page, limit: 20 }) as any);
        }}
      />

      {/* Block/Unblock Confirmation Modal */}
      <BlockUnblockModal
        user={selectedUser}
        action={blockUnblockAction}
        open={showBlockUnblock}
        onClose={() => setShowBlockUnblock(false)}
        onConfirm={handleBlockUnblockConfirm}
        loading={blockUnblockLoading}
      />

      {/* User Transactions Modal */}
      <UserTransactionsModal
        open={showTransactions}
        onClose={() => setShowTransactions(false)}
        userId={selectedUser?.id || ''}
        username={selectedUser?.username || ''}
      />

      {/* User Invites Modal */}
      <UserInvitesModal
        open={showInvites}
        onClose={() => setShowInvites(false)}
        userId={selectedUser?.id || ''}
        username={selectedUser?.username || ''}
      />
    </Box>
  );
};

export default UsersPage; 